// =====================================================================
// SharePackagesPopover — the toolbar "Packages" popover
// =====================================================================
// Two screens in one popover:
//   1. Package cards — one per share flag: file count, what the package is
//      for, a readiness line (ready / need attention / left out) and when it
//      was last saved to the loan.
//   2. Package view — the zip as a read-only tree (pinned "Zip contents" row,
//      then category folders → files), with Show in table beside the zip
//      name and Download zip / Stage on loan in the footer under the
//      "Saved to the loan…" status line.
//
// The tree lists exactly what the backend zips (`getPackageContents`): flag
// on, not hidden, has a stored file. Files that need attention still go in —
// the card is where readiness is shown. Files are added / removed by turning
// the flag on or off (the table's flag columns, or Edit all in the bulk bar).
//
// Both actions save the zip to the loan (Document Packages); Download also
// saves it to your computer. Nothing is delivered anywhere else — the
// backend's push endpoint does the same build + save, so there is no "Push to
// counterparty" here.
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import SearchRounded from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { ink, slate, status, teal } from "../../../../theme/colors";
import {
  DOCUMENT_SHARE_FLAGS,
  getLatestPackageBuild,
  getPackageContents,
} from "../../../../utils/documents/documentShareFlags";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { getDocExpiration } from "./DocExpirationCell";
import { DOC_STATUS_TONES } from "./DocumentInsightCards";
import LoanFilePreviewModal from "../../../../components/modals/files/LoanFilePreviewModal";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { documentStatusLabel } from "../../../../constants/selectOptions/selectOptionsDocumentStatus";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Underwriter accepted and not expired = ready; anything else that still goes
// in the zip needs attention.
const isReady = (doc) =>
  doc?.documentStatus === "Accepted" &&
  getDocExpiration(doc)?.kind !== "expired";

// Why a file in the zip "needs attention" — said in the user's words, so the
// card and the package view can name it rather than just count it.
const attentionReason = (doc) => {
  const parts = [];
  if (getDocExpiration(doc)?.kind === "expired") parts.push("Expired");
  if (doc?.documentStatus !== "Accepted") {
    parts.push(
      `${documentStatusLabel(doc?.documentStatus) || "No status"}, not underwriter accepted yet`,
    );
  }
  return parts.join(" · ");
};
// Why a flagged file is NOT in the zip (mirrors the backend's build filter).
const NOT_INCLUDED_GROUPS = [
  {
    key: "hidden",
    label: "Hidden",
    explain: "Hidden in the documents list. Unhide a file to include it.",
  },
  {
    key: "package",
    label: "Package zips",
    explain: "Built package zips. A package never contains another package.",
  },
  {
    key: "nofile",
    label: "No stored file",
    explain: "There is no stored file to add to the zip.",
  },
];
const notIncludedKey = (doc) => {
  if (String(doc?.docType || "").startsWith("Document Package")) {
    return "package";
  }
  if (
    doc?.isHidden === true ||
    doc?.isHidden === "true" ||
    doc?.isHidden === "True"
  ) {
    return "hidden";
  }
  return "nofile";
};
const notIncludedReason = (doc) =>
  NOT_INCLUDED_GROUPS.find((g) => g.key === notIncludedKey(doc))?.explain;

// The table's status colours, so a file's dot matches its status pill.
const statusDot = (doc) => {
  const tone = DOC_STATUS_TONES.find((t) => t.key === doc?.documentStatus);
  const expired = getDocExpiration(doc)?.kind === "expired";
  return {
    color: tone?.color || slate[300],
    title: [
      documentStatusLabel(doc?.documentStatus) || "No status",
      expired ? "Expired" : null,
    ]
      .filter(Boolean)
      .join(" · "),
  };
};

// Package card border (as specified for the package boxes).
const CARD_BORDER = "#E4E8ED";

const docName = (d) =>
  d?.file_display_name || d?.file_storage_name || "Untitled";

const timeMs = (v) => (v ? new Date(v).getTime() || 0 : 0);

/**
 * Everything the cards and the package view need for one package.
 * `localSave` is a save made in this session ({ at, ids }) — shown right away,
 * before the refetched loan carries the staged zip.
 */
const planPackage = (docs, flag, users, localSave) => {
  const { send, leftOut } = getPackageContents(docs, flag.field);
  const ready = send.filter(isReady);

  const build = getLatestPackageBuild(docs, flag.selector);
  let hist = null;
  if (build) {
    const email = build.sharePackage?.builtBy || build.uploadedBy;
    hist = {
      at: build.sharePackage?.builtAt || build.uploadDate,
      by: getUserFullNameFromEmail(users, email) || email || null,
      ids: build.sharePackage?.documentOids,
    };
  }
  if (localSave && timeMs(localSave.at) > timeMs(hist?.at)) {
    hist = { at: localSave.at, by: "You", ids: localSave.ids };
  }

  // "Added / removed since then" compares the last save's file list with what
  // would go in now (only when the save recorded its list).
  let drift = null;
  if (hist && Array.isArray(hist.ids)) {
    const was = new Set(hist.ids.map(String));
    const now = new Set(send.map((d) => String(d._id)));
    drift = {
      added: [...now].filter((id) => !was.has(id)).length,
      removed: [...was].filter((id) => !now.has(id)).length,
    };
  }
  return {
    send,
    leftOut,
    ready: ready.length,
    attention: send.length - ready.length,
    attentionDocs: send.filter((d) => !isReady(d)),
    hist,
    drift,
    upToDate: !!drift && !drift.added && !drift.removed,
  };
};

const Dot = ({ color }) => (
  <Box
    component="span"
    sx={{
      width: 7,
      height: 7,
      borderRadius: "50%",
      bgcolor: color,
      flex: "none",
    }}
  />
);
Dot.propTypes = { color: PropTypes.string };

const Tag = ({ tone = "neutral", children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      height: 24,
      px: "10px",
      borderRadius: "99px",
      fontSize: 12.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
      bgcolor: tone === "warn" ? status.warning.bg : slate[100],
      color: tone === "warn" ? status.warning.fg : slate[600],
    }}
  >
    {children}
  </Box>
);
Tag.propTypes = { tone: PropTypes.string, children: PropTypes.node };

// "Saved to the loan on 9/12/26 by Jimmy Oliva" + Up to date / "n added since
// then" — the same line on the cards and in the package view.
const HistoryLine = ({ plan, fontSize = 13.5 }) => {
  if (!plan.hist) {
    return (
      <Txt sx={{ fontSize, color: slate[500] }}>Not saved to the loan yet</Txt>
    );
  }
  const { drift } = plan;
  const tag = drift
    ? drift.added || drift.removed
      ? `${[
          drift.added ? `${drift.added} added` : null,
          drift.removed ? `${drift.removed} removed` : null,
        ]
          .filter(Boolean)
          .join(", ")} since then`
      : "Up to date"
    : null;
  return (
    <>
      <Txt sx={{ fontSize, color: slate[500] }}>
        Saved to the loan on {formatDateTime(plan.hist.at, "M/d/yy")}
        {plan.hist.by ? ` by ${plan.hist.by}` : ""}
      </Txt>
      {tag ? (
        <Tag tone={tag === "Up to date" ? "neutral" : "warn"}>{tag}</Tag>
      ) : null}
    </>
  );
};
HistoryLine.propTypes = { plan: PropTypes.object, fontSize: PropTypes.number };

const popHeadSx = {
  px: "20px",
  pt: "16px",
  pb: "14px",
  borderBottom: `1px solid ${slate[200]}`,
  alignItems: "flex-start",
};
const popTitleSx = { fontSize: 18, fontWeight: 600, color: ink, m: 0 };
const popSubSx = { fontSize: 13.5, color: slate[500], mt: "4px" };

// Tooltip body: a heading, then each file with the reason it's counted.
const FileReasonList = ({ heading, docs, reason }) => (
  <Box sx={{ py: "2px" }}>
    <Txt component="div" sx={{ fontSize: 12, fontWeight: 600, mb: "4px" }}>
      {heading}
    </Txt>
    {docs.map((d) => (
      <Txt
        component="div"
        key={d._id}
        sx={{ fontSize: 11.5, lineHeight: 1.45 }}
      >
        {docName(d)}
        <Box component="span" sx={{ opacity: 0.75 }}>
          {" "}
          — {reason(d)}
        </Box>
      </Txt>
    ))}
  </Box>
);
FileReasonList.propTypes = {
  heading: PropTypes.node,
  docs: PropTypes.array,
  reason: PropTypes.func,
};

// ── screen 1: a package card ───────────────────────────────────────────

const PackageCard = ({ flag, plan, onOpen }) => {
  const n = plan.send.length;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`${flag.packageLabel}, ${plural(n, "file")}`}
      sx={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "12px",
        px: "20px",
        py: "16px",
        cursor: "pointer",
        transition: "border-color .12s, background .12s",
        "&:hover": { borderColor: slate[300], bgcolor: slate[50] },
        "&:focus-visible": {
          outline: `2px solid ${teal[600]}`,
          outlineOffset: 2,
        },
      }}
    >
      <Flx ac jb g={1}>
        <Txt sx={{ fontSize: 16, fontWeight: 600, color: ink }}>
          {flag.packageLabel}
        </Txt>
        <Flx ac g={0.25} sx={{ flexShrink: 0 }}>
          <Txt sx={{ fontSize: 13, color: slate[500], whiteSpace: "nowrap" }}>
            {plural(n, "file")}
          </Txt>
          <ChevronRightRounded sx={{ fontSize: 18, color: slate[400] }} />
        </Flx>
      </Flx>
      {flag.packageDescription ? (
        <Txt
          component="div"
          sx={{ fontSize: 13, color: slate[500], mt: "6px" }}
        >
          {flag.packageDescription}
        </Txt>
      ) : null}
      {n || plan.leftOut.length ? (
        <Flx ac g={2} sx={{ mt: "10px", flexWrap: "wrap" }}>
          {plan.ready ? (
            <Flx ac g={0.75}>
              <Dot color={status.success.solid} />
              <Txt sx={{ fontSize: 13.5, color: slate[600] }}>
                {plan.ready} ready
              </Txt>
            </Flx>
          ) : null}
          {plan.attention ? (
            <Tooltip
              placement="top"
              arrow
              title={
                <FileReasonList
                  heading="In the zip, but not ready to send yet"
                  docs={plan.attentionDocs}
                  reason={attentionReason}
                />
              }
            >
              <Flx ac g={0.75}>
                <Dot color={status.warning.solid} />
                <Txt sx={{ fontSize: 13.5, color: status.warning.fg }}>
                  {plan.attention} need attention
                </Txt>
              </Flx>
            </Tooltip>
          ) : null}
          {plan.leftOut.length ? (
            <Tooltip
              placement="top"
              arrow
              title={
                <FileReasonList
                  heading="Flagged for this package, but won't be in the zip"
                  docs={plan.leftOut}
                  reason={notIncludedReason}
                />
              }
            >
              <Flx ac g={0.75}>
                <Dot color={slate[400]} />
                <Txt sx={{ fontSize: 13.5, color: slate[600] }}>
                  {plan.leftOut.length} not included
                </Txt>
              </Flx>
            </Tooltip>
          ) : null}
        </Flx>
      ) : null}
      <Flx ac g={1} sx={{ mt: "10px", flexWrap: "wrap" }}>
        <HistoryLine plan={plan} />
      </Flx>
    </Box>
  );
};
PackageCard.propTypes = {
  flag: PropTypes.object,
  plan: PropTypes.object,
  onOpen: PropTypes.func,
};

// ── screen 2: the package view ─────────────────────────────────────────

const TreeNode = ({
  icon,
  label,
  depth,
  muted,
  onClick,
  dot,
  fontSize = 11.5,
  plain,
}) => (
  <Flx ac g={1} sx={{ py: "5px", pl: `${depth * 22}px`, minWidth: 0 }}>
    <Box sx={{ display: "inline-flex", color: slate[400], flex: "none" }}>
      {icon}
    </Box>
    {dot ? (
      <Tooltip title={dot.title} placement="top" disableInteractive>
        <Box
          component="span"
          aria-label={dot.title}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: dot.color,
            flex: "none",
            ml: "-2px",
          }}
        />
      </Tooltip>
    ) : null}
    <Tooltip
      title={label}
      placement="top-start"
      enterDelay={500}
      disableInteractive
    >
      <Box
        component={onClick ? "button" : "span"}
        type={onClick ? "button" : undefined}
        onClick={onClick}
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize,
          color: muted ? slate[500] : ink,
          ...(onClick && {
            border: 0,
            bgcolor: "transparent",
            p: 0,
            fontFamily: "inherit",
            textAlign: "left",
            cursor: "pointer",
            "&:hover": plain
              ? { color: ink }
              : { color: teal[700], textDecoration: "underline" },
            "&:focus-visible": {
              outline: `2px solid ${teal[600]}`,
              outlineOffset: 1,
            },
          }),
        }}
      >
        {label}
      </Box>
    </Tooltip>
  </Flx>
);
TreeNode.propTypes = {
  dot: PropTypes.object,
  fontSize: PropTypes.number,
  plain: PropTypes.bool,
  icon: PropTypes.node,
  label: PropTypes.string,
  depth: PropTypes.number,
  muted: PropTypes.bool,
  onClick: PropTypes.func,
};

// A collapsible block: chevron + title row, optional hint line, children.
// Closed by default.
const Disclosure = ({ title, hint, small, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          width: "100%",
          border: 0,
          bgcolor: "transparent",
          p: "4px 0",
          ml: "-3px",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          fontSize: small ? 12.5 : 13,
          fontWeight: small ? 500 : 600,
          color: small ? slate[600] : slate[700],
          "&:hover": { color: ink },
          "&:focus-visible": {
            outline: `2px solid ${teal[600]}`,
            outlineOffset: 1,
          },
        }}
      >
        <ExpandMoreRounded
          sx={{
            fontSize: 18,
            color: slate[400],
            transition: "transform .15s",
            transform: open ? "none" : "rotate(-90deg)",
          }}
        />
        {title}
      </Box>
      {open ? (
        <Box sx={{ pb: "4px" }}>
          {hint ? (
            <Txt
              component="div"
              sx={{ fontSize: 12, color: slate[400], pl: "19px", mb: "2px" }}
            >
              {hint}
            </Txt>
          ) : null}
          {children}
        </Box>
      ) : null}
    </Box>
  );
};
Disclosure.propTypes = {
  title: PropTypes.node,
  hint: PropTypes.node,
  small: PropTypes.bool,
  children: PropTypes.node,
};

const tooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 260,
      fontSize: 12.5,
      lineHeight: 1.4,
      bgcolor: ink,
      borderRadius: "8px",
      px: "10px",
      py: "8px",
    },
  },
};

const actionBtnSx = {
  flex: 1,
  height: 40,
  fontSize: 14,
  borderRadius: "10px",
  textTransform: "none",
  whiteSpace: "nowrap",
};

const linkBtnSx = {
  border: 0,
  bgcolor: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  color: teal[700],
  fontSize: 13,
  fontWeight: 500,
  p: "4px 0",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  whiteSpace: "nowrap",
  flex: "none",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: `2px solid ${teal[600]}`, outlineOffset: 1 },
  "& .MuiSvgIcon-root": { fontSize: 15 },
};

const PackageView = ({
  flag,
  plan,
  busyAction,
  filterActive,
  onBack,
  onClose,
  onAction,
  onOpenFile,
  onShowInTable,
}) => {
  const n = plan.send.length;
  // Category folders in the zip tree — open by default, click to fold.
  const [closedFolders, setClosedFolders] = useState(() => new Set());
  const toggleFolder = (folder) =>
    setClosedFolders((cur) => {
      const next = new Set(cur);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  const folders = useMemo(() => {
    const by = new Map();
    plan.send.forEach((d) => {
      const folder = d.docGroup || "No Category";
      if (!by.has(folder)) by.set(folder, []);
      by.get(folder).push(d);
    });
    return [...by.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([folder, files]) => ({
        folder,
        files: [...files].sort((a, b) => docName(a).localeCompare(docName(b))),
      }));
  }, [plan.send]);

  // Staging again with nothing changed only adds a duplicate zip to the loan.
  const stageBlocked = plan.upToDate;

  return (
    <Flx column sx={{ minHeight: 0, maxHeight: "inherit" }}>
      <Flx jb g={1} sx={popHeadSx}>
        <Box sx={{ minWidth: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={onBack}
            sx={{
              ...linkBtnSx,
              p: 0,
              ml: "-4px",
              mb: "12px",
              gap: "2px",
              fontSize: 13,
            }}
          >
            <ChevronLeftRounded sx={{ fontSize: "16px !important" }} />
            All packages
          </Box>
          <Txt component="h3" sx={popTitleSx}>
            {flag.packageLabel}
          </Txt>
          {flag.packageDescription ? (
            <Txt component="div" sx={popSubSx}>
              {flag.packageDescription}
            </Txt>
          ) : null}
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Flx>

      <Box
        sx={{ flex: 1, minHeight: 0, overflow: "auto", px: "20px", pb: "16px" }}
      >
        {/* Pinned: what the zip is called, how much is in it, and the
            table filter for exactly these files. */}
        <Flx
          jb
          g={1.5}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "#fff",
            pt: "14px",
            pb: "10px",
            mb: "6px",
            borderBottom: `1px solid ${slate[200]}`,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Txt component="div" sx={{ fontSize: 12, color: slate[400] }}>
              Zip contents
            </Txt>
            <Flx ac g={1} sx={{ mt: "2px", minWidth: 0 }}>
              <Inventory2Outlined
                sx={{ fontSize: 15, color: slate[400], flex: "none" }}
              />
              <Txt
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: ink,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {flag.packageLabel}.zip
              </Txt>
              <Txt
                sx={{ fontSize: 12.5, color: slate[400], whiteSpace: "nowrap" }}
              >
                {plural(n, "file")}
              </Txt>
            </Flx>
          </Box>
          {n || filterActive ? (
            <Box
              component="button"
              type="button"
              onClick={onShowInTable}
              sx={linkBtnSx}
            >
              {filterActive ? <CloseRounded /> : <SearchRounded />}
              {filterActive ? "Show all files" : "Show in table"}
            </Box>
          ) : null}
        </Flx>

        {n ? (
          <Box role="tree" aria-label={`${flag.packageLabel} zip contents`}>
            {folders.map(({ folder, files }) => (
              <Box key={folder} role="group">
                <TreeNode
                  icon={
                    <Flx ac g={0.25}>
                      <ExpandMoreRounded
                        sx={{
                          fontSize: 16,
                          transition: "transform .15s",
                          transform: closedFolders.has(folder)
                            ? "rotate(-90deg)"
                            : "none",
                        }}
                      />
                      <FolderOutlined sx={{ fontSize: 15 }} />
                    </Flx>
                  }
                  label={`${folder}/`}
                  depth={0}
                  fontSize={12.5}
                  plain
                  onClick={() => toggleFolder(folder)}
                />
                {!closedFolders.has(folder) &&
                  files.map((d) => (
                    <TreeNode
                      key={d._id}
                      icon={<InsertDriveFileOutlined sx={{ fontSize: 15 }} />}
                      label={docName(d)}
                      depth={1.75}
                      muted
                      onClick={() => onOpenFile(d)}
                      dot={statusDot(d)}
                    />
                  ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Txt
            component="p"
            sx={{ fontSize: 13, color: slate[400], m: 0, pt: "6px" }}
          >
            No files in this package yet. Turn on its flag for files in the
            table, or use Edit all in the selection bar, to add them.
          </Txt>
        )}

        {/* Flagged for this package but skipped by the build. Collapsed by
            default; inside, one collapsible group per reason. */}
        {plan.leftOut.length ? (
          <Box
            sx={{
              mt: "14px",
              pt: "10px",
              borderTop: `1px dashed ${slate[200]}`,
            }}
          >
            <Disclosure
              title={`Not included (${plan.leftOut.length})`}
              hint="Flagged for this package, but the zip skips them."
            >
              {NOT_INCLUDED_GROUPS.map((g) => {
                const docs = plan.leftOut.filter(
                  (d) => notIncludedKey(d) === g.key,
                );
                if (!docs.length) return null;
                return (
                  <Box key={g.key} sx={{ pl: "18px" }}>
                    <Disclosure
                      title={`${g.label} (${docs.length})`}
                      hint={g.explain}
                      small
                    >
                      {docs.map((d) => (
                        <TreeNode
                          key={d._id}
                          icon={
                            <InsertDriveFileOutlined sx={{ fontSize: 15 }} />
                          }
                          label={docName(d)}
                          depth={1}
                          muted
                          onClick={() => onOpenFile(d)}
                        />
                      ))}
                    </Disclosure>
                  </Box>
                );
              })}
            </Disclosure>
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${slate[200]}`,
          px: "20px",
          pt: "12px",
          pb: "14px",
          display: "grid",
          gap: "10px",
        }}
      >
        <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
          <HistoryLine plan={plan} fontSize={12.5} />
        </Flx>
        <Flx g={1}>
          <Tooltip
            title="Download the zip to your computer. A copy is also saved to this loan's documents."
            placement="top-start"
            enterDelay={350}
            disableInteractive
            describeChild={false}
            slotProps={tooltipSlotProps}
          >
            <span style={{ flex: 1, display: "flex" }}>
              <Button
                variant="outlined"
                color="gray"
                disabled={!n || !!busyAction}
                onClick={() => onAction("download")}
                startIcon={
                  busyAction === "download" ? (
                    <CircularProgress size={13} sx={{ color: "inherit" }} />
                  ) : (
                    <DownloadOutlined />
                  )
                }
                sx={{ ...actionBtnSx, fontWeight: 500 }}
              >
                Download zip
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              stageBlocked
                ? "Already saved. No files have changed since."
                : "Save the zip to this loan's documents, under Document Packages, without downloading it."
            }
            placement="top-end"
            enterDelay={350}
            disableInteractive
            describeChild={false}
            slotProps={tooltipSlotProps}
          >
            <span style={{ flex: 1, display: "flex" }}>
              <Button
                variant="contained"
                disabled={!n || !!busyAction || stageBlocked}
                onClick={() => onAction("stage")}
                startIcon={
                  busyAction === "stage" ? (
                    <CircularProgress size={13} sx={{ color: "inherit" }} />
                  ) : null
                }
                sx={{ ...actionBtnSx, fontWeight: 600 }}
              >
                Stage on loan
              </Button>
            </span>
          </Tooltip>
        </Flx>
      </Box>
    </Flx>
  );
};
PackageView.propTypes = {
  flag: PropTypes.object,
  plan: PropTypes.object,
  busyAction: PropTypes.string,
  filterActive: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onAction: PropTypes.func,
  onShowInTable: PropTypes.func,
};

// ── the popover ────────────────────────────────────────────────────────

const SharePackagesPopover = ({
  anchorEl,
  onClose,
  docs,
  users,
  busySelector,
  busyAction,
  onDownload,
  onStage,
  activeFilter,
  onToggleFilter,
}) => {
  const open = Boolean(anchorEl);
  // File previews from the package view open in the centred preview modal
  // (with the package's other files to step through), not the side panel.
  const [previewDoc, setPreviewDoc] = useState(null);
  const [view, setView] = useState(null); // selector of the open package, or null
  useEffect(() => {
    if (!open) setView(null);
  }, [open]);

  // Saves made in this session, by selector — so the status line flips to
  // "Saved … by You · Up to date" the moment an action succeeds.
  const [localSaves, setLocalSaves] = useState({});

  const plans = useMemo(
    () =>
      DOCUMENT_SHARE_FLAGS.map((flag) => ({
        flag,
        plan: planPackage(docs, flag, users, localSaves[flag.selector]),
      })),
    [docs, users, localSaves],
  );
  const current = plans.find((p) => p.flag.selector === view);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            mt: "6px",
            width: { xs: "calc(100% - 24px)", sm: 440 },
            maxHeight: "min(640px, calc(100vh - 120px))",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            border: `1px solid ${slate[200]}`,
            boxShadow:
              "0 12px 32px rgba(16,24,32,.16), 0 2px 6px rgba(16,24,32,.08)",
          },
        },
      }}
    >
      {current ? (
        <PackageView
          flag={current.flag}
          plan={current.plan}
          busyAction={
            busySelector === current.flag.selector ? busyAction : null
          }
          filterActive={activeFilter === current.flag.field}
          onBack={() => setView(null)}
          onClose={onClose}
          onOpenFile={setPreviewDoc}
          onShowInTable={() => onToggleFilter(current.flag.field)}
          onAction={(key) => {
            const { flag, plan } = current;
            const ids = plan.send.map((d) => d._id);
            const onSuccess = () =>
              setLocalSaves((s) => ({
                ...s,
                [flag.selector]: { at: new Date().toISOString(), ids },
              }));
            if (key === "download") onDownload(flag, onSuccess);
            else onStage(flag, onSuccess);
          }}
        />
      ) : (
        <Flx column sx={{ minHeight: 0, maxHeight: "inherit" }}>
          <Flx jb g={1} sx={popHeadSx}>
            <Box sx={{ minWidth: 0 }}>
              <Txt component="h3" sx={popTitleSx}>
                Packages
              </Txt>
              <Txt component="div" sx={popSubSx}>
                Each package bundles the files you&apos;ve added to it into one
                zip, with a folder for each category.
              </Txt>
            </Box>
            <IconButton size="small" aria-label="Close" onClick={onClose}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Flx>
          <Box sx={{ overflow: "auto", p: "16px", minHeight: 0 }}>
            <Flx column g={1.5}>
              {plans.map(({ flag, plan }) => (
                <PackageCard
                  key={flag.selector}
                  flag={flag}
                  plan={plan}
                  onOpen={() => setView(flag.selector)}
                />
              ))}
            </Flx>
          </Box>
        </Flx>
      )}
      <LoanFilePreviewModal
        open={!!previewDoc}
        file={previewDoc}
        containerId={previewDoc?.containerName}
        relatedFiles={
          current && current.plan.send.length > 1 ? current.plan.send : null
        }
        onSelectRelatedFile={setPreviewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </Popover>
  );
};

SharePackagesPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  docs: PropTypes.array,
  users: PropTypes.any,
  busySelector: PropTypes.string,
  busyAction: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
  onStage: PropTypes.func.isRequired,
  activeFilter: PropTypes.string,
  onToggleFilter: PropTypes.func.isRequired,
};

export default SharePackagesPopover;
